/**
 * Sales Manager dashboard.
 *
 * Dealer-lens questions this page must answer:
 *   - What do I need to manage the team?  → row 2 (team board)
 *   - What actions do I need to take?     → row 3 (exception queue, actionable inline)
 *   - Where are the opportunities / gaps? → row 1 tiles, row 4 funnel
 *
 * Row 3 is the point of the page. A manager fixes the problem from the list —
 * reassign, hand to AI, set the next step — rather than navigating to four
 * screens to do it.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import KpiTile from '../../components/dashboard/KpiTile';
import { splitSub } from '../../components/dashboard/NewUsedSplit';
import { ExceptionQueue } from '../../components/dashboard/ExceptionList';
import FunnelChart from '../../components/dashboard/FunnelChart';
import { buildFunnelRows } from '../../lib/funnelStages';
import { DonutChart, StackedHBars } from '../../components/dashboard/Pictorial';

const COLS = [
  ['name',                  'Consultant',   false],
  ['openLeads',             'Open leads',   true],
  ['appointmentsToday',     'Appts today',  true],
  ['tasksDue',              'Tasks due',    true],
  ['tasksOverdue',          'Overdue',      true],
  ['testDrives7d',          'Drives 7d',    true],
  ['leadsWithoutNextEvent', 'No next step', true],
  ['conversion',            'Conv.',        true],
];

export default function SalesManagerDashboard({ snapshot, periodLabel, onChanged, widen = null }) {
  const s = snapshot;
  const t = s.today;
  const w = s.window;
  const ex = s.exceptions;

  const [sortKey, setSortKey] = useState('leadsWithoutNextEvent');
  const [sortDir, setSortDir] = useState(-1);
  const [funnelCat, setFunnelCat] = useState('ALL');

  const team = useMemo(() => {
    const rows = Array.isArray(s.team) ? [...s.team] : [];
    rows.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') return av.localeCompare(bv) * sortDir;
      return ((av ?? 0) - (bv ?? 0)) * sortDir;
    });
    return rows;
  }, [s.team, sortKey, sortDir]);

  const sortBy = (k) => {
    if (k === sortKey) setSortDir((d) => -d);
    else { setSortKey(k); setSortDir(k === 'name' ? 1 : -1); }
  };

  return (
    <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {s.legacy && (
        <div className="mono text-xs" style={{ color: 'var(--warning)' }}>
          Showing the legacy snapshot — the team board and exception queue arrive with the role-snapshot endpoint.
        </div>
      )}

      {/* ── ROW 1 ── */}
      <div className="grid-5">
        <KpiTile index={0} label="TEAM APPTS" periodLabel="Today" to="/appointments"
          value={t?.appointments ?? '—'}
          sub={t ? `${t.appointmentsConfirmed ?? 0} confirmed` : undefined} />
        <KpiTile index={1} label="NEW LEADS" periodLabel="Today" to="/leads"
          value={t?.newLeads ?? w.newLeads.total}
          sub={t ? splitSub({ new: t.newLeadsNew, used: t.newLeadsUsed, unknown: t.newLeadsUnknown }) : `${periodLabel} window`} />
        <KpiTile index={2} label="UNACTIONED" periodLabel="> 24h" to="/leads?filter=unactioned"
          value={ex?.unactionedLeads ?? w.stalledLeads}
          color={(ex?.unactionedLeads ?? w.stalledLeads) > 0 ? 'var(--danger)' : 'var(--success)'}
          tone="danger" sub="need contact now" />
        <KpiTile index={3} label="NO NEXT STEP" periodLabel="Now" to="/leads?filter=no-next-step"
          value={ex?.leadsWithoutNextEvent ?? '—'}
          color={(ex?.leadsWithoutNextEvent ?? 0) > 0 ? 'var(--warning)' : 'var(--success)'}
          tone="warning" sub="every lead needs one" />
        <KpiTile index={4} label="TEST DRIVES" periodLabel="Today" to="/test-drives"
          value={t?.testDrives ?? '—'} />
      </div>

      {/* ── ROW 2a: team workload, pictorial ── */}
      {team.length > 0 && (
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>TEAM WORKLOAD</div>
          <StackedHBars
            series={[
              { key: 'open',     label: 'Open leads',   color: 'var(--accent-primary)' },
              { key: 'appts',    label: 'Appts today',  color: 'var(--info)' },
              { key: 'due',      label: 'Tasks due',    color: 'var(--warning)' },
              { key: 'overdue',  label: 'Overdue',      color: 'var(--danger)' },
              { key: 'noNext',   label: 'No next step', color: 'color-mix(in srgb, var(--warning) 50%, var(--bg-secondary))' },
            ]}
            rows={team.map((c) => ({
              key: c.consultantId,
              label: c.name,
              parts: { open: c.openLeads, appts: c.appointmentsToday, due: c.tasksDue, overdue: c.tasksOverdue, noNext: c.leadsWithoutNextEvent },
            }))}
          />
        </div>
      )}

      {/* ── ROW 2: team board ── */}
      <div className="card" style={{ padding: 0 }}>
        <div className="flex-between" style={{ padding: '12px 14px 0' }}>
          <span className="card-label" style={{ margin: 0 }}>TEAM BOARD</span>
          <Link to="/team" className="mono text-xs" style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>manage team</Link>
        </div>
        {!team.length ? (
          <div className="empty-state" style={{ padding: 24 }}>
            <div className="title">{s.team ? 'No consultants yet' : 'Team board needs the role-snapshot endpoint'}</div>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  {COLS.map(([k, label, numeric]) => (
                    <th key={k} onClick={() => sortBy(k)} style={{ cursor: 'pointer', textAlign: numeric ? 'right' : 'left', whiteSpace: 'nowrap' }}>
                      {label}{sortKey === k ? (sortDir > 0 ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {team.map((c) => {
                  const hot = (c.tasksOverdue ?? 0) > 0 || (c.leadsWithoutNextEvent ?? 0) > 0;
                  return (
                    <tr key={c.consultantId} style={hot ? { background: 'color-mix(in srgb, var(--danger) 6%, transparent)' } : undefined}>
                      <td>{c.name}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{c.openLeads ?? 0}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{c.appointmentsToday ?? 0}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{c.tasksDue ?? 0}</td>
                      <td className="mono" style={{ textAlign: 'right', color: c.tasksOverdue > 0 ? 'var(--danger)' : undefined, fontWeight: c.tasksOverdue > 0 ? 700 : 400 }}>{c.tasksOverdue ?? 0}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{c.testDrives7d ?? 0}</td>
                      <td className="mono" style={{ textAlign: 'right', color: c.leadsWithoutNextEvent > 0 ? 'var(--warning)' : undefined, fontWeight: c.leadsWithoutNextEvent > 0 ? 700 : 400 }}>{c.leadsWithoutNextEvent ?? 0}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{c.conversion == null ? '—' : `${Math.round(c.conversion * 100)}%`}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Link to={`/leads?consultant=${c.consultantId}`} className="btn small">View</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── ROW 3: the queue ── */}
      <div className="card">
        <div className="flex-between" style={{ marginBottom: 8 }}>
          <span className="card-label" style={{ margin: 0 }}>EXCEPTION QUEUE</span>
          <Link to="/leads?filter=exceptions" className="mono text-xs" style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>view all</Link>
        </div>
        <ExceptionQueue exceptions={ex} onChanged={onChanged} />
      </div>

      {/* ── ROW 4: funnel + ownership ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        <div className="card">
          <div className="flex-between" style={{ marginBottom: 8 }}>
            <span className="card-label" style={{ margin: 0 }}>FUNNEL · {periodLabel.toUpperCase()}</span>
            <div className="mode-toggle" role="tablist">
              {['ALL', 'NEW', 'USED'].map((c) => (
                <button key={c} role="tab" aria-selected={funnelCat === c} className={funnelCat === c ? 'active' : ''} onClick={() => setFunnelCat(c)}>{c[0] + c.slice(1).toLowerCase()}</button>
              ))}
            </div>
          </div>
          <FunnelChart
            rows={buildFunnelRows(w.funnel, funnelCat)}
            periodLabel={periodLabel}
            widen={widen}
            caption="Sales pipeline by stage"
          />
        </div>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>WHO IS TALKING TO CUSTOMERS</div>
          {s.aiVsHuman ? (
            <DonutChart centerLabel="leads" segments={[
              { label: 'With AI',    value: s.aiVsHuman.aiOwned,    color: 'var(--info)' },
              { label: 'With human', value: s.aiVsHuman.humanOwned, color: 'var(--accent-primary)' },
              { label: 'Shared',     value: s.aiVsHuman.shared,     color: 'var(--warning)' },
            ]} />
          ) : <div className="mono text-xs text-muted">Needs role-snapshot.</div>}
        </div>
      </div>
    </div>
  );
}
